import { Button, DatePicker, DatePickerProps, Select } from 'antd'
import React, { useState } from 'react'

import { InfoCircleOutlined } from '@ant-design/icons'
import TextArea from 'antd/es/input/TextArea'
import { useParams } from 'react-router-dom'
import { TERMINATION_REASONS } from '../../../Types'
import { useTerminateSubject } from '../../../hooks/useSubject'
import { REASON_CHARS_MINIMUM } from '../../../constants'
import dayjs from 'dayjs'
import { disableFutureDates } from '../../../util/disableFutureTime'

export const TerminateSubjectDrawer = ({ subject, onClose = () => {} }) => {
  const { projectId, siteId } = useParams() as { projectId: string; siteId: string }

  const [disqualificationType, setDisqualificationType] = useState<TERMINATION_REASONS>()
  const [disqualificationReason, setDisqualificationReason] = useState('')
  const [reasonError, setReasonError] = useState<string | null>(null)
  const [disqualificationTypeError, setDisqualificationTypeError] = useState<string | null>(null)

  const [date, setDate] = useState<any>(dayjs())

  const [dateTimeError, setDateTimeError] = useState<string | null>(null)

  const [timePanelChange, setTimePanelChange] = useState<any>()

  const { callTerminate, isLoading } = useTerminateSubject(projectId, subject._id, siteId)

  const [locked, setLocked] = useState(false)

  const resetState = () => {
    setDisqualificationType(undefined)
    setDisqualificationReason('')
    onClose()
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setReasonError(null)
    setDisqualificationReason(e.target.value)
  }

  const handleDisqualificationTypeChange = (value: TERMINATION_REASONS) => {
    setDisqualificationTypeError(null)
    setDisqualificationType(value)
  }

  const handleTerminate = () => {
    // If there are disqualification type chosen -> set error and leave
    if (!disqualificationType) {
      setDisqualificationTypeError(`Please provide a termination reason`)
      return
    }

    // If the number of characters in the reason text field is less than the required minimum  -> set error and leave
    if (disqualificationReason.length < REASON_CHARS_MINIMUM) {
      setReasonError(`Please provide a reason minimum ${REASON_CHARS_MINIMUM} characters long`)
      return
    }

    if (date === null) {
      setDateTimeError('Please provide a valid date and time for termination')
      return
    }

    // Keep the picked calendar day and set the time to 1 minute after midnight UTC,
    // so the sent date is the same day regardless of the clinician's timezone
    // Issue #707
    const combined = date.utc(true).hour(0).minute(1).second(0).millisecond(0)

    const obj = {
      projectId: projectId,
      subjectId: subject._id,
      reasonType: disqualificationType,
      reasonDescription: disqualificationReason,
      // Why? fetch serializes dates as ISO strings so we send strings regardless
      // Undefined would mean that the Clinician did not choose a custom termination date, so the backend should set it to the current date and time
      terminationDate: locked ? combined.toISOString() : undefined,
    }

    return callTerminate(obj)
      .then(() => {
        resetState()
      })
      .catch((error) => {
        alert(error.message)
      })
  }

  const handlePanelChange = (open) => {
    setTimePanelChange(open)
  }

  const handleDate: DatePickerProps['onChange'] = (date) => {
    setDate(date)
    setTimePanelChange(false)
    setDateTimeError(null)
  }

  return (
    <div className="container-fluid h-100 d-flex flex-column">
      <div className="d-flex align-items-center mb-3">
        <InfoCircleOutlined style={{ color: '#dc3545', fontSize: 22, marginRight: 8 }} />
        <h5 style={{ margin: 0 }}>Confirm Study Termination</h5>
      </div>
      <p className="mt-3 mb-1">
        Please select the reason for termination for the audit trail. The participants’s device will be automatically
        disabled, stopping all data collection.
      </p>
      <hr></hr>
      <p className="">Participant was terminated at a different date? You can enable custom termination date below.</p>
      <p className="mb-2 opacity-75 mt-2 p-small">Termination date</p>
      <div className="mb-4 d-flex align-items-center">
        {!locked ? (
          <Button className="me-2" onClick={() => setLocked(!locked)}>
            Enable date
          </Button>
        ) : null}
        <DatePicker
          width={100}
          defaultValue={dayjs()}
          needConfirm={false}
          allowClear={true}
          format="DD-MM-YYYY"
          onChange={handleDate}
          className="me-2"
          disabledDate={(current) => disableFutureDates(current, subject.createdAt)}
          showNow={false}
          onOpenChange={handlePanelChange}
          disabled={!locked}
          style={{ width: 120 }}
        />

        <p className="p-small text-danger">{dateTimeError || '\u00A0'}</p>
      </div>
      <p className="opacity-75 p-small mb-2">Termination reason</p>
      <Select
        placeholder="Reason"
        onChange={(value: TERMINATION_REASONS) => handleDisqualificationTypeChange(value)}
        value={disqualificationType}
        options={[
          { value: TERMINATION_REASONS.DISCONTINUED, label: 'Discontinued' },
          { value: TERMINATION_REASONS.SCREEN_FAILURE, label: 'Screen failure' },
          { value: TERMINATION_REASONS.OTHER, label: 'Other' },
        ]}
        className="me-2 w-100"
      />
      <p className="p-small text-danger">{disqualificationTypeError || '\u00A0'}</p>
      <p className="opacity-75 p-small  mb-2">Provide justification</p>
      <TextArea
        placeholder="Justification for study termination"
        rows={2}
        className=""
        value={disqualificationReason}
        onChange={handleTextChange}
      />
      <p className="p-small text-danger">{reasonError || '\u00A0'}</p>
      <div className="mt-3" style={{ textAlign: 'right' }}>
        <Button onClick={resetState}>Cancel</Button>
        <Button
          className="ms-2"
          type="primary"
          danger
          loading={isLoading}
          onClick={handleTerminate}
          disabled={timePanelChange}
        >
          Confirm Termination
        </Button>
      </div>
    </div>
  )
}
